import {
  createFileRoute, redirect,
} from '@tanstack/react-router';
import AdditionalThirdParties from '../../../../components/pages/additional-third-parties';
import {
  ApiError,
  HttpStatus,
  isAdditionalThirdPartiesNotFound,
  isAdditionalThirdPartiesUnauthorized,
  isNotFoundError,
  isUnauthorizedError,
  NotFoundErrorCode,
  apiError,
} from '../../../../utils/errors';
import { getSettingsByUrlSlug } from '../../../../api/settings/get-by-url-slug';
import { SurveySettings, ThirdPartyType } from '../../../../types';
import { getAdditionalThirdPartiesData } from '../../../../api/submissions/get-additional-third-parties';

export interface AdditionalThirdPartiesSearchParams {
  token?: string;
}

interface AdditionalThirdPartiesLoaderParams {
  params: { accountSlug: string; submissionId: string, type: string};
  deps: AdditionalThirdPartiesSearchParams;
}

export interface AdditionalThirdPartiesLoaderResponse {
  settings: SurveySettings;
  type?: ThirdPartyType;
  error?: ApiError;
}

const validThirdPartyTypes = new Set<string>(Object.values(ThirdPartyType));

const isValidThirdPartyType = (type: string): type is ThirdPartyType =>
  validThirdPartyTypes.has(type);

async function additionalThirdPartiesLoader ({
  params: {
    accountSlug, submissionId, type,
  },
  deps: { token },
}: AdditionalThirdPartiesLoaderParams): Promise<AdditionalThirdPartiesLoaderResponse> {
  try {
    if (!isValidThirdPartyType(type)) {
      throw apiError(HttpStatus.NOT_FOUND, `Invalid third party type: ${type}`, NotFoundErrorCode.AdditionalThirdPartiesNotFound);
    }

    const additionalThirdPartiesData = await getAdditionalThirdPartiesData(accountSlug, submissionId, token, type);

    return {
      settings: additionalThirdPartiesData.settings,
      type: additionalThirdPartiesData.type,
    };
  } catch (error) {
    if (isNotFoundError(error) && error.code === NotFoundErrorCode.SettingsNotFound) {
      throw redirect({ to: '/' });
    }

    if (isAdditionalThirdPartiesNotFound(error) ||
      isAdditionalThirdPartiesUnauthorized(error) ||
      isUnauthorizedError(error) ||
      (isNotFoundError(error) && error.code === NotFoundErrorCode.SubmissionNotFound)) {
      const settings = await getSettingsByUrlSlug(accountSlug);
      return {
        settings,
        error,
        type: type as ThirdPartyType,
      };
    }

    throw error;
  }
}

/**
 * Returns route configuration options for additional third parties routes
 * Can be spread directly in the route configuration
 */
function additionalThirdPartiesRouteLoader () {
  return {
    // Extract loader dependencies - fallback to search for backward compat
    loaderDeps: ({ search }: { search: AdditionalThirdPartiesSearchParams }): AdditionalThirdPartiesSearchParams => ({
      token: search.token,
    }),

    // Load data using the validated params
    loader: async ({ params, deps }: AdditionalThirdPartiesLoaderParams) =>
      additionalThirdPartiesLoader({
        params,
        deps,
      }),
  };
}

export const Route = createFileRoute('/$accountSlug/$submissionId/additional-third-parties/$type')({
  ...additionalThirdPartiesRouteLoader(),
  component: AdditionalThirdParties,
});
